import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, User, LogOut, ChevronDown, Phone, Mail } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { CONTACT_INFO, categories } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null); // 'shop' | 'about' | 'support' | null
  const { cartCount, openCart } = useCart();
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setOpenMenu(null);
  }, [location]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const aboutLinks = [
    { name: 'About Us', path: '/about' },
    { name: 'Our Mission', path: '/our-mission' },
    { name: 'Why Choose Us', path: '/why-choose-us' },
    { name: 'Quality Guarantee', path: '/quality-guarantee' },
  ];

  const supportLinks = [
    { name: 'FAQ', path: '/faq' },
    { name: 'Shipping', path: '/shipping' },
    { name: 'Returns & Refunds', path: '/returns' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms & Conditions', path: '/terms' },
  ];

  const isAboutActive = aboutLinks.some(l => location.pathname === l.path);
  const isSupportActive = supportLinks.some(l => location.pathname === l.path);

  const DropdownPanel = ({ items, primaryLink, primaryLabel }) => (
    <>
      <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
      <div className="absolute top-full left-0 mt-3 w-72 bg-white rounded-sm border-t-4 border-athletic-500 shadow-2xl z-20 p-2">
        {primaryLink && (
          <Link to={primaryLink} className="block px-4 py-3 text-sm text-ink-900 hover:bg-athletic-50 hover:text-athletic-600 font-bold uppercase tracking-wider border-b border-ink-100">
            {primaryLabel}
          </Link>
        )}
        {items.map((item) => (
          <Link
            key={item.path}
            to={item.path}
            className="block px-4 py-2.5 text-sm text-ink-700 hover:bg-athletic-50 hover:text-athletic-600 transition-colors"
          >
            {item.name}
          </Link>
        ))}
      </div>
    </>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Utility Bar */}
      <div className={`transition-all duration-300 bg-ink-950 text-white ${isScrolled ? 'h-0 opacity-0 overflow-hidden' : 'h-10 opacity-100'}`}>
        <div className="max-w-7xl mx-auto h-full px-4 lg:px-8 flex items-center justify-between text-[11px] uppercase tracking-[0.2em]">
          <div className="hidden md:flex items-center gap-5 text-white/70">
            <a href={`tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`} className="flex items-center gap-2 hover:text-athletic-400 transition-colors">
              <Phone className="w-3 h-3" /> {CONTACT_INFO.phone}
            </a>
            <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-2 hover:text-athletic-400 transition-colors">
              <Mail className="w-3 h-3" /> {CONTACT_INFO.email}
            </a>
          </div>
          <p className="text-white flex-1 md:flex-none text-center">
            <span className="text-athletic-400 font-bold">FREE USA SHIPPING</span>
            <span className="mx-2 text-white/40">•</span>
            On Orders $75+
          </p>
          <div className="hidden md:block text-white/50 text-[10px]">Level Up Your Training</div>
        </div>
      </div>

      {/* Main Header */}
      <nav className={`transition-all duration-300 border-b ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-xl py-3 border-ink-100 shadow-sm'
          : 'bg-white py-4 border-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between gap-6">

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
              <div className="relative w-11 h-11 bg-athletic-500 group-hover:bg-athletic-600 transition-colors flex items-center justify-center overflow-hidden">
                <span className="absolute -right-2 top-0 w-0 h-0 border-t-[44px] border-t-ink-900 border-l-[16px] border-l-transparent" />
                <span className="relative text-white font-display text-2xl leading-none pt-1">S</span>
              </div>
              <div className="flex flex-col leading-tight">
                <span className="font-display text-ink-900 text-2xl leading-none tracking-wider">
                  SPORT<span className="text-athletic-500">FITT</span>PRO
                </span>
                <span className="text-[9px] text-ink-400 font-semibold tracking-[0.35em] uppercase mt-0.5">
                  Gear. Grit. Glory.
                </span>
              </div>
            </Link>

            {/* Navigation */}
            <div className="hidden lg:flex items-center gap-0.5">
              <Link to="/" className={`px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors ${location.pathname === '/' ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'}`}>
                Home
              </Link>

              {/* Shop dropdown */}
              <div className="relative">
                <button
                  onClick={() => setOpenMenu(openMenu === 'shop' ? null : 'shop')}
                  className={`flex items-center gap-1 px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors ${location.pathname.includes('/shop') || location.pathname.includes('/product') ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'}`}
                >
                  Shop
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'shop' ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === 'shop' && (
                  <DropdownPanel
                    primaryLink="/shop"
                    primaryLabel="All Products"
                    items={categories.map(c => ({ name: c.name, path: `/shop?category=${c.slug}` }))}
                  />
                )}
              </div>

              {/* About dropdown */}
              <div className="relative">
                <button
                  onClick={() => setOpenMenu(openMenu === 'about' ? null : 'about')}
                  className={`flex items-center gap-1 px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors ${isAboutActive ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'}`}
                >
                  About
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'about' ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === 'about' && <DropdownPanel items={aboutLinks} />}
              </div>

              {/* Support dropdown */}
              <div className="relative">
                <button
                  onClick={() => setOpenMenu(openMenu === 'support' ? null : 'support')}
                  className={`flex items-center gap-1 px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors ${isSupportActive ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'}`}
                >
                  Support
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'support' ? 'rotate-180' : ''}`} />
                </button>
                {openMenu === 'support' && <DropdownPanel items={supportLinks} />}
              </div>

              <Link to="/contact" className={`px-3 py-2 text-[13px] font-bold uppercase tracking-wider transition-colors ${location.pathname === '/contact' ? 'text-athletic-500' : 'text-ink-800 hover:text-athletic-500'}`}>
                Contact
              </Link>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              <div className="hidden lg:block">
                {isAuthenticated ? (
                  <button onClick={handleLogout} className="p-2.5 text-ink-700 hover:text-athletic-500 transition-colors" title="Sign Out">
                    <LogOut className="w-5 h-5" />
                  </button>
                ) : (
                  <Link to="/login" className="p-2.5 text-ink-700 hover:text-athletic-500 transition-colors">
                    <User className="w-5 h-5" />
                  </Link>
                )}
              </div>

              <button onClick={openCart} className="relative flex items-center gap-2 pl-3 pr-4 py-2.5 bg-ink-900 hover:bg-athletic-500 text-white transition-all group" data-testid="cart-button">
                <ShoppingBag className="w-5 h-5" />
                <span className="hidden sm:inline font-bold text-xs uppercase tracking-wider">Cart</span>
                <span className="flex items-center justify-center min-w-[22px] h-[22px] px-1 bg-athletic-500 group-hover:bg-white text-white group-hover:text-athletic-500 text-[11px] font-black rounded-full">
                  {cartCount}
                </span>
              </button>

              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 text-ink-900">
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        <div className={`lg:hidden transition-all duration-300 overflow-hidden ${isMobileMenuOpen ? 'max-h-[85vh] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="bg-white border-t border-ink-100 px-4 py-4 space-y-1 max-h-[75vh] overflow-y-auto">

            <Link to="/" className={`block px-4 py-3 text-sm font-bold uppercase tracking-wider border-l-4 ${location.pathname === '/' ? 'border-athletic-500 bg-athletic-50 text-athletic-600' : 'border-transparent text-ink-800'}`}>
              Home
            </Link>
            <Link to="/shop" className={`block px-4 py-3 text-sm font-bold uppercase tracking-wider border-l-4 ${location.pathname === '/shop' ? 'border-athletic-500 bg-athletic-50 text-athletic-600' : 'border-transparent text-ink-800'}`}>
              Shop
            </Link>

            {/* Mobile: About group */}
            <div className="pt-2">
              <p className="px-4 pb-1 text-[10px] font-black uppercase tracking-[0.3em] text-ink-400">About</p>
              {aboutLinks.map(l => (
                <Link key={l.path} to={l.path} className={`block px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-l-4 ${location.pathname === l.path ? 'border-athletic-500 bg-athletic-50 text-athletic-600' : 'border-transparent text-ink-800'}`}>
                  {l.name}
                </Link>
              ))}
            </div>

            {/* Mobile: Support group */}
            <div className="pt-2">
              <p className="px-4 pb-1 text-[10px] font-black uppercase tracking-[0.3em] text-ink-400">Support</p>
              {supportLinks.map(l => (
                <Link key={l.path} to={l.path} className={`block px-4 py-2.5 text-sm font-bold uppercase tracking-wider border-l-4 ${location.pathname === l.path ? 'border-athletic-500 bg-athletic-50 text-athletic-600' : 'border-transparent text-ink-800'}`}>
                  {l.name}
                </Link>
              ))}
            </div>

            <Link to="/contact" className={`block px-4 py-3 text-sm font-bold uppercase tracking-wider border-l-4 mt-2 ${location.pathname === '/contact' ? 'border-athletic-500 bg-athletic-50 text-athletic-600' : 'border-transparent text-ink-800'}`}>
              Contact
            </Link>

            <div className="pt-3 mt-3 border-t border-ink-100">
              {isAuthenticated ? (
                <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-3 text-sm font-bold uppercase text-ink-800">
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              ) : (
                <Link to="/login" className="flex items-center gap-3 px-4 py-3 text-sm font-bold uppercase text-ink-800">
                  <User className="w-4 h-4" /> Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
